import {
  KoButton,
  KoDrawer,
  KoForm,
  KoLoader,
  KoModal,
  KoSelect,
  KoTable,
  KoText,
  KoTitle,
  KoTag,
  KoToast,
  KoEmpty,
  KoPagination
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { ApiPlatformAPI } from '../services/apiplatform.service';
import { COLUMNS_RELEASE_TESTS, TEST_RESULT_CONFIG, HTTP_METHOD_CONFIG } from '../constants';
import ReleaseTestCrud from './crud/ReleaseTestCrud';
import ApiSubNav from './ApiSubNav';
import styles from './apiplatform.module.scss';

const MOCK_SERVICE_LIST = [
  { label: 'User Service', value: 'svc-001' },
  { label: 'Payment Gateway', value: 'svc-002' },
  { label: 'Notification Hub', value: 'svc-003' },
  { label: 'Search Engine', value: 'svc-004' }
];

const MOCK_ENV_LIST = [
  { label: 'Development', value: 'env-001' },
  { label: 'Staging', value: 'env-002' },
  { label: 'Production', value: 'env-003' }
];

const MOCK_TEST_SUITES = [
  { _id: 'ts-001', name: 'User CRUD Suite', serviceName: 'User Service', endpointsCount: 8, passed: 7, failed: 1, skipped: 0, status: 'failed', duration: 12.4, lastRun: '2026-02-24T10:30:00Z' },
  { _id: 'ts-002', name: 'Payment Flow Suite', serviceName: 'Payment Gateway', endpointsCount: 6, passed: 6, failed: 0, skipped: 0, status: 'passed', duration: 18.2, lastRun: '2026-02-24T09:15:00Z' },
  { _id: 'ts-003', name: 'Auth & Token Suite', serviceName: 'User Service', endpointsCount: 5, passed: 4, failed: 0, skipped: 1, status: 'passed', duration: 8.6, lastRun: '2026-02-24T08:00:00Z' },
  { _id: 'ts-004', name: 'Search Integration', serviceName: 'Search Engine', endpointsCount: 4, passed: 3, failed: 1, skipped: 0, status: 'failed', duration: 6.1, lastRun: '2026-02-23T18:00:00Z' },
  { _id: 'ts-005', name: 'Notification Delivery', serviceName: 'Notification Hub', endpointsCount: 7, passed: 7, failed: 0, skipped: 0, status: 'passed', duration: 22.8, lastRun: '2026-02-23T14:45:00Z' },
  { _id: 'ts-006', name: 'Payment Refund Suite', serviceName: 'Payment Gateway', endpointsCount: 3, passed: 0, failed: 0, skipped: 3, status: 'skipped', duration: 0, lastRun: '2026-02-22T11:30:00Z' }
];

const MOCK_TEST_RESULTS = [
  { _id: 'tc-001', status: 'passed', method: 'GET', path: '/v3/users', actualStatus: 200, duration: 45 },
  { _id: 'tc-002', status: 'passed', method: 'POST', path: '/v3/users', actualStatus: 201, duration: 120 },
  { _id: 'tc-003', status: 'failed', method: 'PUT', path: '/v3/users/:id', actualStatus: 500, duration: 230 },
  { _id: 'tc-004', status: 'passed', method: 'GET', path: '/v3/users/:id', actualStatus: 200, duration: 28 },
  { _id: 'tc-005', status: 'passed', method: 'DELETE', path: '/v3/users/:id', actualStatus: 204, duration: 55 }
];

function ReleaseTests() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [suites, setSuites] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ size: 20, page: 1 });

  const [isModalOpen, setModalOpen] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const [selectedRunEnv, setSelectedRunEnv] = useState(null);

  const [serviceList, setServiceList] = useState([]);
  const [environmentList, setEnvironmentList] = useState([]);
  const [form] = KoForm.useForm();

  const basePath = workspaceId
    ? `/workspace/${workspaceId}/development/apiplatform`
    : '/apiplatform';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'API Platform', path: basePath },
      { title: 'Release Tests' }
    ]));
    fetchLists();
  }, [workspaceId]);

  useEffect(() => {
    fetchSuites();
  }, [workspaceId, pagination]);

  const fetchLists = () => {
    ApiPlatformAPI.getServiceList().then(
      (data) => {
        const list = (data?.data || data || []).map(s => ({ label: s.name, value: s._id || s.serviceId }));
        setServiceList(list.length > 0 ? list : MOCK_SERVICE_LIST);
      },
      () => setServiceList(MOCK_SERVICE_LIST)
    );
    ApiPlatformAPI.getEnvironmentList().then(
      (data) => {
        const list = (data?.data || data || []).map(e => ({ label: e.name, value: e._id || e.environmentId }));
        setEnvironmentList(list.length > 0 ? list : MOCK_ENV_LIST);
      },
      () => setEnvironmentList(MOCK_ENV_LIST)
    );
  };

  const fetchSuites = () => {
    setLoading(true);
    ApiPlatformAPI.getTestSuites({ page: pagination.page, limit: pagination.size }).then(
      (res) => {
        const data = res?.data || res || [];
        setSuites(data.length > 0 ? data : MOCK_TEST_SUITES);
        setTotalCount(res?.totalCount || (data.length > 0 ? data.length : MOCK_TEST_SUITES.length));
        setLoading(false);
      },
      () => {
        setSuites(MOCK_TEST_SUITES);
        setTotalCount(MOCK_TEST_SUITES.length);
        setLoading(false);
      }
    );
  };

  const handleSave = (values) => {
    setLoading(true);
    ApiPlatformAPI.createTestSuite(values, currentRecord?._id).then(
      () => {
        setLoading(false);
        setModalOpen(false);
        form.resetFields();
        setCurrentRecord(null);
        fetchSuites();
        KoToast.success(currentRecord ? 'Test suite updated.' : 'Test suite created.');
      },
      () => { setLoading(false); KoToast.error('Failed to save test suite.'); }
    );
  };

  const handleDelete = () => {
    setLoading(true);
    ApiPlatformAPI.deleteTestSuite(currentRecord._id || currentRecord.suiteId).then(
      () => {
        setLoading(false);
        setDeleteOpen(false);
        setCurrentRecord(null);
        fetchSuites();
        KoToast.success('Test suite deleted.');
      },
      () => { setLoading(false); KoToast.error('Delete failed.'); }
    );
  };

  const openDetail = (record) => {
    setSelectedSuite(record);
    setDrawerOpen(true);
    ApiPlatformAPI.getTestResults(record._id || record.suiteId).then(
      (res) => {
        const data = res?.data || res?.testCases || res || [];
        setTestResults(data.length > 0 ? data : MOCK_TEST_RESULTS);
      },
      () => setTestResults(MOCK_TEST_RESULTS)
    );
  };

  const handleRunSuite = () => {
    if (!selectedSuite) return;
    setLoading(true);
    ApiPlatformAPI.runTestSuite(selectedSuite._id || selectedSuite.suiteId, selectedRunEnv).then(
      () => {
        setLoading(false);
        KoToast.success('Test suite execution started.');
        ApiPlatformAPI.getTestResults(selectedSuite._id || selectedSuite.suiteId).then(
          (res) => {
            const data = res?.data || res?.testCases || res || [];
            setTestResults(data.length > 0 ? data : MOCK_TEST_RESULTS);
          },
          () => setTestResults(MOCK_TEST_RESULTS)
        );
        fetchSuites();
      },
      () => { setLoading(false); KoToast.error('Failed to run test suite.'); }
    );
  };

  const columnsWithActions = [
    ...COLUMNS_RELEASE_TESTS,
    {
      label: '', id: 'actions', key: 'actions', width: 120,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <KoButton
            iconBtn
            icon={<KoIcon name='EyeOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); openDetail(record); }}
          />
          <KoButton
            iconBtn
            icon={<KoIcon name='EditOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); setCurrentRecord(record); form.setFieldsValue(record); setModalOpen(true); }}
          />
          <KoButton
            iconBtn
            danger
            icon={<KoIcon name='DeleteOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); setCurrentRecord(record); setDeleteOpen(true); }}
          />
        </div>
      )
    }
  ];

  return (
    <div className='tableCard'>
      {isLoading && <KoLoader />}
      <ApiSubNav activeKey='release-tests' />

      <KoToolBar
        title='Release Tests'
        search={{ visible: true, placeholder: 'Search test suites...', onChange: () => {} }}
        filter={{ visible: false, options: [] }}
        buttonList={[{
          label: 'New Test Suite',
          type: 'primary',
          icon: <KoIcon color='#FFF' name='PlusOutlined' width={18} height={18} />,
          onClick: () => { setCurrentRecord(null); form.resetFields(); setModalOpen(true); }
        }]}
      />

      {suites.length === 0 && !isLoading && <KoEmpty description='No test suites configured' />}
      {suites.length > 0 && (
        <>
          <KoTable maxHeight={480} dataSource={suites} columns={columnsWithActions} rowKey={(r) => r._id || r.suiteId} />
          <KoPagination totalCount={totalCount} pageSize={pagination.size} currentPage={pagination.page} onChange={(page, size) => setPagination({ page, size })} />
        </>
      )}

      {/* Test Suite Detail Drawer */}
      <KoDrawer
        title='Test Suite Detail'
        open={isDrawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedSuite(null); setTestResults([]); setSelectedRunEnv(null); }}
        width={'min(700px, 95vw)'}
      >
        {selectedSuite && (
          <div>
            <div className={styles.drawerHeader}>
              <KoTitle level={4} text={selectedSuite.name} />
              <KoTag color={TEST_RESULT_CONFIG[selectedSuite.status]?.color}>
                {TEST_RESULT_CONFIG[selectedSuite.status]?.label || selectedSuite.status || 'N/A'}
              </KoTag>
            </div>

            <div className={styles.detailMetaGrid}>
              <div className={styles.detailMetaItem}>
                <span className={styles.detailLabel}>Service</span>
                <span className={styles.detailValue}>{selectedSuite.serviceName || '—'}</span>
              </div>
              <div className={styles.detailMetaItem}>
                <span className={styles.detailLabel}>Last Run</span>
                <span className={styles.detailValue}>{selectedSuite.lastRun || '—'}</span>
              </div>
              <div className={styles.detailMetaItem}>
                <span className={styles.detailLabel}>Duration</span>
                <span className={styles.detailValue}>{selectedSuite.duration ? `${selectedSuite.duration}s` : '—'}</span>
              </div>
              <div className={styles.detailMetaItem}>
                <span className={styles.detailLabel}>Endpoints</span>
                <span className={styles.detailValue}>{selectedSuite.endpointsCount || 0}</span>
              </div>
            </div>

            {/* Run Controls */}
            <div className={styles.detailSection}>
              <div className={styles.detailLabel}>Run Suite</div>
              <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', marginTop: '0.8rem' }}>
                <KoSelect
                  style={{ minWidth: 200 }}
                  value={selectedRunEnv}
                  onChange={setSelectedRunEnv}
                  placeholder='Select environment'
                  options={environmentList}
                  allowClear
                />
                <KoButton
                  type='primary'
                  label='Run Suite'
                  onClick={handleRunSuite}
                  icon={<KoIcon name='PlayCircleOutlined' width={14} height={14} color='#FFF' />}
                />
              </div>
            </div>

            {/* Test Cases */}
            <div className={styles.detailSection}>
              <div className={styles.detailLabel}>Test Cases ({testResults.length})</div>
              {testResults.length === 0 && <KoText text='No test results available. Run the suite first.' />}
              <div className={styles.testCaseList}>
                {testResults.map((tc, i) => {
                  const isPassed = tc.status === 'passed';
                  const isFailed = tc.status === 'failed';
                  const mCfg = HTTP_METHOD_CONFIG[tc.method] || {};
                  return (
                    <div key={i} className={styles.testCaseItem}>
                      <KoTag color={mCfg.color}>{mCfg.label || tc.method}</KoTag>
                      <span className={styles.testCaseEndpoint}>{tc.path || tc.endpoint}</span>
                      <div className={`${styles.testCaseStatus} ${isPassed ? styles.testCasePassed : isFailed ? styles.testCaseFailed : ''}`}>
                        <KoIcon name={isPassed ? 'CheckCircleOutlined' : isFailed ? 'CloseCircleOutlined' : 'MinusCircleOutlined'} width={14} height={14} />
                        {tc.actualStatus || tc.status}
                      </div>
                      <span className={styles.testCaseDuration}>{tc.duration ? `${tc.duration}ms` : '—'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </KoDrawer>

      {/* Create/Edit Modal */}
      <KoModal
        title={currentRecord ? 'Edit Test Suite' : 'New Test Suite'}
        centered
        open={isModalOpen}
        handleOk={() => setModalOpen(false)}
        onClose={() => { setModalOpen(false); setCurrentRecord(null); }}
        width={'min(700px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => { setModalOpen(false); setCurrentRecord(null); }} />,
          <KoButton key='save' type='primary' loading={isLoading} label='Save' onClick={() => form.submit()} />
        ]}
      >
        <ReleaseTestCrud form={form} handleFinish={handleSave} services={serviceList} environments={environmentList} />
      </KoModal>

      {/* Delete Confirmation */}
      <KoModal
        title='Delete Test Suite'
        centered
        open={isDeleteOpen}
        handleOk={() => setDeleteOpen(false)}
        onClose={() => { setDeleteOpen(false); setCurrentRecord(null); }}
        width={'min(440px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => { setDeleteOpen(false); setCurrentRecord(null); }} />,
          <KoButton key='delete' type='primary' danger loading={isLoading} label='Delete' onClick={handleDelete} />
        ]}
      >
        <div className={styles.confirmBody}>
          <KoTitle level={5} text={`Are you sure you want to delete "${currentRecord?.name}"?`} />
          <KoText text='This action cannot be undone.' />
        </div>
      </KoModal>
    </div>
  );
}

export default ReleaseTests;
